import { icons as localLucideIcons } from 'lucide';

const SVG_NS = 'http://www.w3.org/2000/svg';

export const toPascalCase = (value = '') =>
  value
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const splitClasses = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(/\s+/)
    .map((cls) => cls.trim())
    .filter(Boolean);
};

export const mergeClassNames = (...classValues) =>
  Array.from(new Set(classValues.flatMap(splitClasses))).filter(Boolean).join(' ');

export const resolveIconRegistry = (lucideInstance) =>
  (lucideInstance && lucideInstance.icons) || lucideInstance || localLucideIcons || null;

export const getIconNode = (registry, iconName) => {
  if (!registry || !iconName) {
    return null;
  }
  const pascalName = toPascalCase(iconName);
  return registry[pascalName] || registry[iconName] || null;
};

export const buildLucideSvgElement = (
  registry,
  iconName,
  { width, height, strokeWidth, className, ariaHidden } = {}
) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const iconNode = getIconNode(registry, iconName);
  if (!iconNode) {
    return null;
  }

  const [tag, baseAttrs = {}, children = []] = iconNode;
  const attrs = {
    ...baseAttrs,
    width: width ?? baseAttrs.width ?? 24,
    height: height ?? baseAttrs.height ?? 24,
    'stroke-width': strokeWidth ?? baseAttrs['stroke-width'] ?? 2,
    'data-lucide': iconName || baseAttrs['data-lucide']
  };

  const mergedClass = mergeClassNames(
    'lucide',
    iconName ? `lucide-${iconName}` : null,
    baseAttrs.class,
    className
  );
  if (mergedClass) {
    attrs.class = mergedClass;
  }

  if (!('aria-hidden' in attrs)) {
    attrs['aria-hidden'] = ariaHidden ?? 'true';
  }

  const createSvg = ([nodeTag, nodeAttrs = {}, nodeChildren = []]) => {
    const el = document.createElementNS(SVG_NS, nodeTag);
    Object.entries(nodeAttrs).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      el.setAttribute(key, String(value));
    });
    nodeChildren.forEach((child) => {
      const childEl = createSvg(child);
      if (childEl) {
        el.appendChild(childEl);
      }
    });
    return el;
  };

  return createSvg([tag, attrs, children]);
};

export { localLucideIcons };
